import { getStage } from 'src/api/config';
import { Alert } from '@amzn/awsui-components-react-v3';
import React from 'react';
import { GroupMetadata, TeamInfo, AccountMetadata, ProjectMetadata, Metadata } from 'aws-sdk/clients/omnismusservice';

export type GroupEntry = GroupMetadata &
  TeamInfo & {
    groupId: string;
    ctiString: string;
  };
export type AccountEntry = AccountMetadata & { accountId: string; groupName: string; teamId: string };
export type ProjectEntry = ProjectMetadata & { projectId: string; accountId: string; groupName: string };

export function groupMetadataToGroupEntry(groupMetadata: GroupMetadata): GroupEntry {
  const cti = groupMetadata.teamInfo?.primaryCti;
  return {
    ...groupMetadata,
    ...groupMetadata.teamInfo,
    groupId: getResourceIdFromMetadataId(groupMetadata.metadataId),
    ctiString: cti ? `${cti.category}/${cti.type}/${cti.item}` : '-',
  };
}

export function accountMetadataToAccountEntry(
  accountMetadata: AccountMetadata,
  groupName: string,
  teamId: string,
): AccountEntry {
  return {
    ...accountMetadata,
    accountId: getResourceIdFromMetadataId(accountMetadata.metadataId),
    groupName: groupName,
    teamId: teamId,
  };
}

export function projectMetadataToProjectEntry(projectMetadata: ProjectMetadata, groupName: string): ProjectEntry {
  return {
    ...projectMetadata,
    projectId: getResourceIdFromMetadataId(projectMetadata.metadataId),
    accountId: getResourceIdFromMetadataId(projectMetadata.parentMetadataId.accountMetadataId),
    groupName: groupName,
  };
}

export function extractGroupResources(
  group: GroupEntry,
  metadataList: Metadata[],
): { newAccounts: AccountEntry[]; newProjects: ProjectEntry[] } {
  const newAccounts: AccountEntry[] = metadataList
    .map((metadata) => metadata.accountMetadata)
    .filter((account): account is AccountMetadata => !!account)
    .map((account) => accountMetadataToAccountEntry(account, group.name, group.teamId));
  const newProjects: ProjectEntry[] = metadataList
    .map((metadata) => metadata.projectMetadata)
    .filter((project): project is ProjectMetadata => !!project)
    .map((project) => projectMetadataToProjectEntry(project, group.name));
  return { newAccounts, newProjects };
}

export const SMUS_BETA_IAD = 'https://dzd_6hev6c1hjnriw7.sagemaker.us-east-1.on.aws';
export const SMUS_GAMMA_IAD = 'https://dzd_clmu6316n831pj.sagemaker.us-east-1.on.aws';
export const SMUS_PROD_IAD = 'https://dzd-5mt141ugvv3w13.sagemaker.us-east-1.on.aws';

export const getSmusUrl = () => {
  const stage = getStage();
  switch (stage) {
    case 'local':
      return SMUS_BETA_IAD;
    case 'beta':
      return SMUS_GAMMA_IAD;
    case 'gamma_iad':
      return SMUS_GAMMA_IAD;
    case 'prod_iad':
      return SMUS_PROD_IAD;
    default:
      return '';
  }
};

export const getSmusProjectUrl = (projectId: string): string => {
  return `${getSmusUrl()}/projects/${projectId}`;
};

export const getSmusCreateProjectUrl = (): string => {
  return `${getSmusUrl()}/projects/create`;
};

export function getResourceIdFromMetadataId(metadataId: string): string {
  if (!metadataId) return '';
  const parts = metadataId.split('#');
  return parts.length > 1 ? parts[1] : metadataId;
}

export const OmniSmusUnavailableHeader = () => {
  return (
    <Alert type={'error'} header={'Could not load page'}>
      AWS-SMUS is not available for this region and stage.
    </Alert>
  );
};
